import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  List,
  ListItem,
  TextField,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Chip,
  FormControl,
  MenuItem,
  Select,
  InputAdornment,
} from "@mhome/ui";
import { OutlinedInput } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import DevicesIcon from "@mui/icons-material/Devices";
import { getIntegration } from "../service/integration-service";
import {
  createEntityTwinCard,
  batchCreateEntityTwinCard,
} from "../service/ui-service";
import { useWebSocket } from "../ws/websocket";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import LoadingSpinner from "../common/loading";
import { useToast } from "../common/toast";
import { toEntityTitleSubtitle } from "../common/string-helpers";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const UpsertDeviceDialog = ({
  open,
  onClose,
  onBeforeSave,
  onSave,
  currentCanvasId = null,
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [optionsList, setOptionsList] = useState([]);
  const [twinTags, setTwinTags] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);

  const { sendAndWaitWs } = useWebSocket();
  const [loading, setLoading] = useState(false);
  const { successToast, errorToast } = useToast();
  const [loadingSave, setLoadingSave] = useState(false);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const [meta, setMeta] = useState({ twinNames: {} });
  const getComponentBgColor = useComponentBackgroundColor();

  const getTwinName = (twinTag) => {
    return (meta?.twinNames && meta.twinNames[twinTag]) || twinTag;
  };

  // Get unique integrations from options
  const availableIntegrations = useMemo(() => {
    if (!optionsList) return [];

    const integrationsWithDevices = new Set();
    optionsList.forEach((option) => {
      if (option.integration) {
        integrationsWithDevices.add(option.integration);
      }
    });

    return Array.from(integrationsWithDevices).sort();
  }, [optionsList]);

  // Get grouped capabilities from options - group by display name, combine twin tags
  const availableCapabilities = useMemo(() => {
    if (!optionsList) return [];

    const capabilityGroups = new Map();

    optionsList.forEach((option) => {
      if (option.twinTags && Array.isArray(option.twinTags)) {
        option.twinTags.forEach((tag) => {
          const displayName = getTwinName(tag);

          if (!capabilityGroups.has(displayName)) {
            capabilityGroups.set(displayName, {
              displayName,
              twinTags: new Set(),
            });
          }

          capabilityGroups.get(displayName).twinTags.add(tag);
        });
      }
    });

    // Convert to array and sort by display name
    return Array.from(capabilityGroups.values())
      .map((group) => ({
        ...group,
        twinTags: Array.from(group.twinTags),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [optionsList, meta]);

  // Filter and sort options
  const filteredOptions = useMemo(() => {
    let filtered = optionsList.filter((option) => {
      // Name filter
      if (filterText.trim()) {
        const searchWords = filterText.toLowerCase().trim().split(/\s+/);
        const optionName = option.name.toLowerCase();
        const matchesName = searchWords.every((word) =>
          optionName.includes(word)
        );
        if (!matchesName) return false;
      }

      // Integration filter
      if (selectedIntegrations.length > 0) {
        if (!selectedIntegrations.includes(option.integration)) {
          return false;
        }
      }

      // Capabilities filter
      if (selectedCapabilities.length > 0) {
        const optionTwinTags = option.twinTags || [];

        // Get all twin tags for selected capabilities
        const selectedTwinTags = new Set();
        selectedCapabilities.forEach((capabilityName) => {
          const capability = availableCapabilities.find(
            (cap) => cap.displayName === capabilityName
          );
          if (capability) {
            capability.twinTags.forEach((tag) => selectedTwinTags.add(tag));
          }
        });

        const hasMatchingTag = optionTwinTags.some((tag) =>
          selectedTwinTags.has(tag)
        );
        if (!hasMatchingTag) return false;
      }

      return true;
    });

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    optionsList,
    filterText,
    selectedIntegrations,
    selectedCapabilities,
    availableCapabilities,
  ]);

  useEffect(() => {
    if (open) {
      handleLoadIntegration();
    } else {
      setSelectedItems([]);
      setTwinTags([]);
      setOptionsList([]);
      setFilterText("");
      setSelectedIntegrations([]);
      setSelectedCapabilities([]);
    }
  }, [open]);

  const handleLoadIntegration = async () => {
    setLoading(true);
    setOptionsList([]);
    try {
      const resp = await getIntegration(sendAndWaitWs, "basic.device");

      if (resp) {
        // Get all entities and groups from all enabled types
        const fetchedDevices = [];
        const fetchedGroups = [];

        // Process entities from all enabled types
        if (resp.meta?.entityEnabled) {
          Object.entries(resp.meta.entityEnabled).forEach(([type, enabled]) => {
            if (enabled && resp.entities[type]) {
              fetchedDevices.push(...resp.entities[type]);
            }
          });
        }

        // Process groups from all enabled types
        if (resp.meta?.groupEnabled) {
          Object.entries(resp.meta.groupEnabled).forEach(([type, enabled]) => {
            if (enabled && resp.groups[type]) {
              fetchedGroups.push(...resp.groups[type]);
            }
          });
        }

        setMeta(resp.meta || { twinNames: {} });

        const combinedOptions = [
          ...fetchedDevices.map((device) => ({
            type: "device",
            id: device.id,
            name: device.name || "Unnamed Device",
            integration: device.integration,
            twinTags: device.twinTags || [],
            original: device,
          })),
          ...fetchedGroups.map((group) => ({
            type: "group",
            id: group.groupId,
            name: `${group.name} (Group)` || "Unnamed Group",
            integration: group.integration || "basic.device",
            twinTags: group.twinTags || [],
            original: group,
          })),
        ];
        setOptionsList(combinedOptions);
      } else {
        errorToast("Error loading devices and groups");
        console.debug("Fetch integration error! Response was:", resp);
      }
    } catch (error) {
      errorToast("Error loading devices and groups");
      console.error("Error loading integration data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrationChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedIntegrations(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCapabilityChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedCapabilities(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleItemToggle = (option) => {
    const isSelected = selectedItems.some(
      (selected) => selected.type === option.type && selected.id === option.id
    );

    let newSelectedItems;
    if (isSelected) {
      // Remove item
      newSelectedItems = selectedItems.filter(
        (selected) =>
          !(selected.type === option.type && selected.id === option.id)
      );
    } else {
      // Add item
      newSelectedItems = [...selectedItems, option];
    }

    setSelectedItems(newSelectedItems);

    // Update twin tags
    if (newSelectedItems.length > 0) {
      const allTwinTags = new Set();
      newSelectedItems.forEach((item) => {
        if (item.type === "device" || item.type === "group") {
          const selectedDeviceData = item.original;
          if (selectedDeviceData && selectedDeviceData.twinTags) {
            selectedDeviceData.twinTags.forEach((tag) => allTwinTags.add(tag));
          }
        }
      });

      const uniqueTwinTags = Array.from(allTwinTags);
      setTwinTags(uniqueTwinTags);
    } else {
      setTwinTags([]);
    }
  };

  const handleClose = () => {
    if (loadingSave) {
      return;
    }
    onClose();
  };

  const handleAdd = async () => {
    if (!selectedItems || selectedItems.length === 0) {
      errorToast("Please select at least one device or group.");
      return;
    }

    // Check if selected devices have capabilities
    const hasCapabilities = selectedItems.some(
      (item) => item.original.twinTags && item.original.twinTags.length > 0
    );
    if (!hasCapabilities) {
      errorToast("Selected devices have no available capabilities.");
      return;
    }

    setLoadingSave(true);
    if (onBeforeSave) onBeforeSave();

    try {
      if (selectedItems.length === 1) {
        // Single device: use existing createEntityTwinCard
        const selectedItem = selectedItems[0];
        const availableTagsToSave = selectedItem.original.twinTags || [];

        if (availableTagsToSave.length > 0) {
          const resp = await createEntityTwinCard(
            sendAndWaitWs,
            currentCanvasId,
            selectedItem.integration,
            selectedItem.id,
            selectedItem.name,
            selectedItem.original.type,
            availableTagsToSave,
            selectedItem.type === "group",
            selectedItem.original.twinSig
          );

          if (resp) {
            successToast("Device card created successfully!");
            if (onSave) onSave();
            handleClose();
          } else {
            errorToast("Error creating device card.");
          }
        } else {
          errorToast("Selected device has no available capabilities.");
        }
      } else {
        // Multiple devices: use batchCreateEntityTwinCard
        // Group items by integration and entityType
        const groupedItems = {};

        selectedItems.forEach((selectedItem) => {
          if (
            selectedItem.original.twinTags &&
            selectedItem.original.twinTags.length > 0
          ) {
            const key = `${selectedItem.integration}-${selectedItem.original.type}`;
            if (!groupedItems[key]) {
              groupedItems[key] = {
                integration: selectedItem.integration,
                entityType: selectedItem.original.type,
                items: [],
              };
            }

            groupedItems[key].items.push({
              entityId: selectedItem.id,
              entityName: selectedItem.name,
              twinTags: selectedItem.original.twinTags,
              group: selectedItem.type === "group",
              twinSig: selectedItem.original.twinSig,
            });
          }
        });

        let hasErrors = false;

        // Process each group separately
        for (const group of Object.values(groupedItems)) {
          try {
            const resp = await batchCreateEntityTwinCard(
              sendAndWaitWs,
              currentCanvasId,
              group.integration,
              group.entityType,
              group.items
            );

            if (resp && resp.success) {
              // pass
            } else {
              hasErrors = true;
              console.error(
                `Batch save failed for ${group.integration}-${group.entityType}`
              );
            }
          } catch (error) {
            hasErrors = true;
            console.error(
              `Error in batch save for ${group.integration}-${group.entityType}:`,
              error
            );
          }
        }

        if (!hasErrors) {
          successToast("Created device cards");
          if (onSave) onSave();
          handleClose();
        } else {
          errorToast("Error!");
        }
      }
    } catch (error) {
      errorToast(
        selectedItems.length === 1
          ? "Error creating device card."
          : "Error creating device cards."
      );
      console.error("Error saving twins:", error);
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        elevation: 0,
        style: isSmallScreen
          ? {
              boxShadow: "none",
              borderRadius: 0,
              width: "100%",
              maxWidth: "100%",
              minWidth: "100%",
              marginTop: 0,
              marginBottom: 0,
              height: "100vh",
              maxHeight: "100vh",
              paddingTop: `${safeAreaInsets.top}px`,
              paddingBottom: `${safeAreaInsets.bottom}px`,
              paddingRight: `${safeAreaInsets.right}px`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : {
              width: "600px",
              maxWidth: "600px",
              minWidth: "600px",
              marginTop: `${safeAreaInsets.top}px !important`,
              marginBottom: `${safeAreaInsets.bottom}px !important`,
              height: `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
              maxHeight: `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
              paddingTop: 0,
              paddingBottom: 0,
              paddingRight: `${safeAreaInsets.right}px`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
      }}
    >
      <DialogTitle className="gap-2 flex items-center min-h-0">
        <IconButton aria-label="close" onClick={onClose} className="shrink-0">
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" component="div" className="leading-[1.2] mb-0">
          New Device Card
        </Typography>
      </DialogTitle>

      <DialogContent className="px-4 pt-4 pb-2 flex flex-col flex-1 min-h-0 overflow-auto">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="flex flex-col gap-4 mt-2">
            {/* Filters Card */}
            <Paper
              variant="outlined"
              className="p-4"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                className="flex items-center gap-2 mb-4"
              >
                <FilterListIcon fontSize="small" /> Filters
              </Typography>
              <div className="flex flex-col gap-4">
                {/* Search Field */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search devices and groups..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Filter Dropdowns */}
                <div className="flex gap-4 flex-wrap">
                  {/* Integration filter */}
                  <div className="flex-1 min-w-[150px]">
                    <Typography
                      variant="caption"
                      className="font-medium mb-1 block"
                      style={{
                        color: "hsl(var(--muted-foreground))",
                      }}
                    >
                      Provider
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        value={selectedIntegrations}
                        onChange={handleIntegrationChange}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                          <div className="flex flex-wrap gap-1">
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={meta?.integrationNames?.[value] || value}
                                size="small"
                                className="h-6"
                              />
                            ))}
                          </div>
                        )}
                        MenuProps={MenuProps}
                      >
                        {availableIntegrations.map((integration) => (
                          <MenuItem key={integration} value={integration}>
                            <Checkbox
                              checked={
                                selectedIntegrations.indexOf(integration) > -1
                              }
                              className="mr-2 p-1"
                            />
                            <ListItemText
                              primary={
                                meta?.integrationNames?.[integration] ||
                                integration
                              }
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Twin tags filter */}
                  <div className="flex-1 min-w-[150px]">
                    <Typography
                      variant="caption"
                      className="font-medium mb-1 block"
                      style={{
                        color: "hsl(var(--muted-foreground))",
                      }}
                    >
                      Capability
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        value={selectedCapabilities}
                        onChange={handleCapabilityChange}
                        input={<OutlinedInput />}
                        renderValue={(selected) => (
                          <div className="flex flex-wrap gap-1">
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                className="h-6"
                              />
                            ))}
                          </div>
                        )}
                        MenuProps={MenuProps}
                      >
                        {availableCapabilities.map((capability) => (
                          <MenuItem
                            key={capability.displayName}
                            value={capability.displayName}
                          >
                            <Checkbox
                              checked={
                                selectedCapabilities.indexOf(
                                  capability.displayName
                                ) > -1
                              }
                              className="mr-2 p-1"
                            />
                            <ListItemText primary={capability.displayName} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </div>
            </Paper>

            {/* Devices List Card */}
            <Paper
              variant="outlined"
              className="p-4"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              <Typography
                variant="subtitle2"
                className="flex items-center gap-2 mb-4"
              >
                <DevicesIcon fontSize="small" /> Devices & Groups
              </Typography>
              <List className="w-full bg-transparent">
                {filteredOptions.map((option) => {
                  const isSelected = selectedItems.some(
                    (selected) =>
                      selected.type === option.type && selected.id === option.id
                  );

                  const hasCapabilities =
                    option.twinTags && option.twinTags.length > 0;

                  const isAlreadyCreated = !!(
                    option.original.cardCount && option.original.cardCount > 0
                  );

                  const isNewVersion = option.original.newTwin === true;

                  let title = option.name;
                  let subtitle = option.type === "device" ? "Device" : "Group";

                  const parsed = toEntityTitleSubtitle(option.name, true);
                  if (typeof parsed === "object" && parsed.title) {
                    title = parsed.title;
                    subtitle = parsed.subtitle;
                  }

                  return (
                    <ListItem
                      key={`${option.type}-${option.id}`}
                      button
                      onClick={() =>
                        !isAlreadyCreated && handleItemToggle(option)
                      }
                      disabled={isAlreadyCreated}
                      className="rounded"
                      style={{
                        opacity: isAlreadyCreated ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isAlreadyCreated) {
                          e.currentTarget.style.backgroundColor =
                            "hsl(var(--accent))";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAlreadyCreated) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <ListItemIcon className="min-w-[40px]">
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          disabled={isAlreadyCreated}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <div className="flex items-center gap-2">
                            <Typography
                              variant="body2"
                              className="font-medium text-sm leading-[1.2] line-clamp-2 overflow-hidden text-ellipsis flex-1"
                              style={{
                                color: isAlreadyCreated
                                  ? "hsl(var(--muted-foreground))"
                                  : "hsl(var(--foreground))",
                              }}
                            >
                              {title}
                            </Typography>
                            <div className="flex gap-2">
                              {!hasCapabilities && (
                                <Chip
                                  label="No capabilities"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  className="h-5 text-[0.65rem] font-medium"
                                />
                              )}
                              {isNewVersion && (
                                <Chip
                                  label="New version"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  className="h-5 text-[0.65rem] font-medium"
                                />
                              )}
                              {isAlreadyCreated ? (
                                <Chip
                                  label="Already added"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                  className="h-5 text-[0.65rem] font-medium"
                                />
                              ) : (
                                <></>
                              )}
                            </div>
                          </div>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color={
                              isAlreadyCreated
                                ? "text.disabled"
                                : "textSecondary"
                            }
                            noWrap
                            className="mt-1"
                          >
                            {subtitle}
                          </Typography>
                        }
                        className="m-0"
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </div>
        )}
      </DialogContent>

      <DialogActions className="pb-4 px-4">
        <Button
          onClick={handleClose}
          disabled={loadingSave}
          variant="outlined"
          className="normal-case"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!selectedItems.length || loadingSave}
          className="normal-case"
        >
          {loadingSave ? (
            <CircularProgress size={20} />
          ) : (
            `Add ${
              selectedItems.length > 1
                ? `${selectedItems.length} Cards`
                : "Card"
            }`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertDeviceDialog;
